import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '@/lib/utils/date';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { StatusFilterPills } from '@/components/common/StatusFilterPills';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import type { Bill, BillFilters, BillState } from '../types';

interface BillsDataTableProps {
  data: Bill[];
  columns: ColumnDef<Bill>[];
  isLoading: boolean;
  filters: BillFilters;
  updateFilter: <K extends keyof BillFilters>(key: K, value: BillFilters[K]) => void;
  updateFilters?: (updates: Partial<BillFilters>) => void;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

// PENDING is retired (2026-07-17) and gets no filter pill — no bill can reach
// it any more. The badge still renders it so legacy rows stay readable.
const BILL_STATES: BillState[] = ['DRAFT', 'UNPAID', 'PAID', 'ARCHIVED'];

const stateActiveClass: Record<BillState, string> = {
  DRAFT: 'data-[state=on]:bg-gray-500',
  PENDING: 'data-[state=on]:bg-amber-500',
  UNPAID: 'data-[state=on]:bg-red-500',
  PAID: 'data-[state=on]:bg-emerald-500',
  ARCHIVED: 'data-[state=on]:bg-slate-500',
};

export function BillsDataTable({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  updateFilters,
  onClearFilters,
  controlsContainer,
  tableZoom: tableZoomProp,
}: BillsDataTableProps) {
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('bills');
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  const { state, 'dueAt.min': dueMin, 'dueAt.max': dueMax } = filters;

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.clientId) count++;
    if (filters.contractId) count++;
    if (filters.state) count++;
    if (filters['dueAt.min'] || filters['dueAt.max']) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Toolbar: status pills + table controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-2">
        <StatusFilterPills
          value={state}
          onChange={(v) => updateFilter('state', v as BillState | undefined)}
          options={BILL_STATES.map((s) => ({
            value: s,
            label: t(s.toLowerCase()),
            activeClassName: stateActiveClass[s],
          }))}
        />
        <div className="flex items-center gap-2">
          {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      {/* Filters panel */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={4}
      >
        {/* Due Date Range */}
        <FilterField label={t('dueDateRange')}>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
              >
                <span className="truncate">
                  {dueMin && dueMax
                    ? `${format(new Date(dueMin), 'MM/dd/yy', { locale: dateFnsLocale })} - ${format(new Date(dueMax), 'MM/dd/yy', { locale: dateFnsLocale })}`
                    : dueMin
                      ? `${format(new Date(dueMin), 'MM/dd/yy', { locale: dateFnsLocale })} —`
                      : dueMax
                        ? `— ${format(new Date(dueMax), 'MM/dd/yy', { locale: dateFnsLocale })}`
                        : t('all')}
                </span>
                <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar
                mode="range"
                selected={{
                  from: dueMin ? new Date(dueMin) : undefined,
                  to: dueMax ? new Date(dueMax) : undefined,
                }}
                onSelect={(range) => {
                  if (updateFilters) {
                    updateFilters({
                      'dueAt.min': range?.from?.toISOString(),
                      'dueAt.max': range?.to?.toISOString(),
                    });
                  } else {
                    updateFilter('dueAt.min', range?.from?.toISOString());
                    updateFilter('dueAt.max', range?.to?.toISOString());
                  }
                }}
                numberOfMonths={2}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </FilterField>
        {/* Client */}
        <FilterField label={t('client')}>
          <UniversalCombobox
            url="/clients"
            labelKey="name"
            labelKeys={['lastname', 'name', 'middlename']}
            valueKey="id"
            selectedId={filters.clientId}
            onSelectItem={(client) =>
              updateFilter('clientId', client ? (client['id'] as number) : undefined)
            }
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>

        {/* Contract */}
        <FilterField label={t('contract')}>
          <UniversalCombobox
            url="/contracts"
            listUrl="/contracts/select-box"
            labelKey="name"
            valueKey="id"
            selectedId={filters.contractId}
            onSelectItem={(contract) =>
              updateFilter('contractId', contract ? (contract['id'] as number) : undefined)
            }
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>

      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="hover:bg-[#f8fafc]">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noBillsFound')}</h3>
                    <p className="text-[#64748b]">{t('trackBillsMessage')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
